/**
 * Authentication context and hooks for managing user authentication state.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  User,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithCustomToken as firebaseSignInWithCustomToken,
} from 'firebase/auth';
import { auth } from '../utils/firebaseSetUp';
import { isSelfHosted, AUTH_SESSION_EVENT } from '../config/infra';
import { getSelfHostedUser, clearSelfHostedSession } from '../api/selfHostedAuth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<void>;
  signInWithCustomToken: (token: string) => Promise<User>;
  organizationId: string | null;
  userRole: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const applyStoredSession = () => {
      const stored = getSelfHostedUser();
      if (stored) {
        setUser({ uid: stored.uid, email: stored.email } as User);
        setOrganizationId(stored.organizationId ?? null);
        setUserRole(stored.role ?? null);
      } else {
        setUser(null);
        setOrganizationId(null);
        setUserRole(null);
      }
      setLoading(false);
    };

    if (isSelfHosted) {
      applyStoredSession();
      window.addEventListener(AUTH_SESSION_EVENT, applyStoredSession);
      return () => window.removeEventListener(AUTH_SESSION_EVENT, applyStoredSession);
    }

    const unsubscribe = onAuthStateChanged(auth, async user => {
      setUser(user);

      if (user) {
        try {
          // Get custom claims to extract organization ID and role
          const tokenResult = await user.getIdTokenResult();
          const claims = tokenResult.claims;

          setOrganizationId((claims.organization_id as string) || null);
          setUserRole((claims.role as string) || null);
        } catch (error) {
          console.error('Error getting user claims:', error);
          setOrganizationId(null);
          setUserRole(null);
        }
      } else {
        setOrganizationId(null);
        setUserRole(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async (): Promise<void> => {
    try {
      if (isSelfHosted) {
        clearSelfHostedSession();
        setUser(null);
        setOrganizationId(null);
        setUserRole(null);
        return;
      }
      await firebaseSignOut(auth);
      // Clear any stored data
      localStorage.removeItem('authToken');
      localStorage.removeItem('organizationId');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const signInWithCustomToken = async (token: string): Promise<User> => {
    try {
      const userCredential = await firebaseSignInWithCustomToken(auth, token);
      return userCredential.user;
    } catch (error) {
      console.error('Error signing in with custom token:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    signOut,
    signInWithCustomToken,
    organizationId,
    userRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Additional hooks for common use cases
export const useCurrentUser = (): User | null => {
  const { user } = useAuth();
  return user;
};

export const useOrganizationId = (): string | null => {
  const { organizationId } = useAuth();
  return organizationId;
};

export const useUserRole = (): string | null => {
  const { userRole } = useAuth();
  return userRole;
};

export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
};

export const useAuthLoading = (): boolean => {
  const { loading } = useAuth();
  return loading;
};
